"use client";

import {diffWordsWithSpace} from "diff";
import {useTranslation} from "react-i18next";

type Segment = {kind: "same"; value: string} | {kind: "change"; removed: string; added: string};

/**
 * One reading order: each edited phrase shows what goes, then what replaces it.
 * Shared spaces inside an edited phrase belong to both sides, so the struck and
 * inserted text each read as a continuous phrase.
 */
export function diffSegments(before: string, after: string): Segment[] {
   const parts = diffWordsWithSpace(before, after);
   const segments: Segment[] = [];
   parts.forEach((part, index) => {
      const sharedSpace = !part.added && !part.removed && /^\s+$/.test(part.value) &&
         (parts[index - 1]?.added || parts[index - 1]?.removed) &&
         (parts[index + 1]?.added || parts[index + 1]?.removed);
      const previous = segments.at(-1);
      if (!part.added && !part.removed && !sharedSpace) {
         if (previous?.kind === "same") previous.value += part.value;
         else segments.push({kind: "same", value: part.value});
         return;
      }
      const change = previous?.kind === "change" ? previous : {kind: "change" as const, removed: "", added: ""};
      if (change !== previous) segments.push(change);
      if (!part.added) change.removed += part.value;
      if (!part.removed) change.added += part.value;
   });
   // Whitespace both sides share at an edit's edges is unchanged text, not part of the edit.
   return segments.flatMap(segment => {
      if (segment.kind === "same") return [segment];
      const lead = sharedWhitespace(segment.removed, segment.added, "start");
      const trail = sharedWhitespace(segment.removed.slice(lead.length), segment.added.slice(lead.length), "end");
      const trim = (value: string) => value.slice(lead.length, value.length - trail.length);
      return [
         ...(lead ? [{kind: "same" as const, value: lead}] : []),
         {kind: "change" as const, removed: trim(segment.removed), added: trim(segment.added)},
         ...(trail ? [{kind: "same" as const, value: trail}] : []),
      ];
   });
}

/** `\s` as a single-character test. A trailing `\s+$` scan backtracks across the whole string. */
function isDiffSpace(char: string | undefined): boolean {
   return !!char && /\s/.test(char);
}

function whitespaceRun(value: string, edge: "start" | "end"): string {
   if (edge === "start") {
      let end = 0;
      while (isDiffSpace(value[end])) end++;
      return value.slice(0, end);
   }
   let start = value.length;
   while (isDiffSpace(value[start - 1])) start--;
   return value.slice(start);
}

function sharedWhitespace(removed: string, added: string, edge: "start" | "end"): string {
   const fromRemoved = whitespaceRun(removed, edge);
   const fromAdded = whitespaceRun(added, edge);
   if (!fromRemoved || !fromAdded || !removed.trim() || !added.trim()) return "";
   let length = 0;
   const limit = Math.min(fromRemoved.length, fromAdded.length);
   while (length < limit && (edge === "start"
      ? fromRemoved[length] === fromAdded[length]
      : fromRemoved[fromRemoved.length - 1 - length] === fromAdded[fromAdded.length - 1 - length])) length++;
   return edge === "start" ? fromRemoved.slice(0, length) : fromRemoved.slice(fromRemoved.length - length);
}

function segmentKey(segment: Segment, seen: Map<string, number>): string {
   const body = segment.kind === "same" ? segment.value : `${segment.removed}\0${segment.added}`;
   const count = (seen.get(body) ?? 0) + 1;
   seen.set(body, count);
   return count === 1 ? `${segment.kind}:${body}` : `${segment.kind}:${body}#${count}`;
}

function DiffSegment({segment, side}: {segment: Segment; side: "before" | "after"}) {
   if (segment.kind === "same") return <span>{segment.value}</span>;
   if (side === "before") {
      return segment.removed
         ? <del className="rounded-sm bg-red-100 text-red-800 no-underline dark:bg-red-950/60 dark:text-red-200">{segment.removed}</del>
         : null;
   }
   return segment.added
      ? <ins className="rounded-sm bg-green-100 text-green-900 no-underline dark:bg-green-950/60 dark:text-green-200">{segment.added}</ins>
      : null;
}

/** Before and after side by side, each marking only its own edits. Stacks when the container is narrow. */
export function TextDiff({before, after}: {before: string; after: string}) {
   const {t} = useTranslation("ask");
   const segments = diffSegments(before, after);
   const side = (kind: "before" | "after") => {
      const seen = new Map<string, number>();
      return <div data-side={kind} className="min-w-0 rounded-lg border border-300 bg-panel px-3 py-2">
         <p className="mb-1 text-xs font-medium text-500">{t(`toolApproval.${kind}`)}</p>
         <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">
            {segments.map(segment => <DiffSegment key={segmentKey(segment, seen)} segment={segment} side={kind} />)}
         </p>
      </div>;
   };
   return <div className="@container">
      <div className="grid gap-2 @lg:grid-cols-2">{side("before")}{side("after")}</div>
   </div>;
}
